import React, { useContext, useState } from "react";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { Button, CircularProgress, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Grid from "@mui/material/GridLegacy";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import * as Yup from "yup";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { NotificationType, SERVER_URL } from "../../constants/global";
import { UNIQUE_USER } from "../../queries/pharmacy/pharmacyTypeDefs";
import { useMutationUpdateBankAccount } from "../../queries/bankAccount/hooks/useMutationUpdateBankAccount";
import { useAuth } from "../../hooks/useAuth";
import { UserContext } from "../../hooks/userContext";

const INK = "#1E293B";
const INK_SOFT = "#475569";
const LINE = "#E2E8F0";
const LINE_SOFT = "#F1F5F9";
const PRIMARY = "#2563EB";
const PRIMARY_DARK = "#1D4ED8";
const BODY_FONT = '"Source Sans 3", sans-serif';

const textFieldSx = {
  fontFamily: BODY_FONT,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontFamily: BODY_FONT,
    "& fieldset": { borderColor: LINE },
    "&:hover fieldset": { borderColor: "#475569" },
    "&.Mui-focused fieldset": { borderColor: PRIMARY, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: PRIMARY },
};

const primaryBtnSx = {
  bgcolor: PRIMARY,
  color: "#FFFFFF",
  borderRadius: "8px",
  fontWeight: 600,
  fontFamily: BODY_FONT,
  textTransform: "none" as const,
  boxShadow: "none",
  px: 2.5,
  "&:hover": { bgcolor: PRIMARY_DARK, boxShadow: "none" },
};

const outlinedBtnSx = {
  borderRadius: "8px",
  fontWeight: 600,
  fontFamily: BODY_FONT,
  textTransform: "none" as const,
  borderColor: LINE,
  color: INK_SOFT,
  "&:hover": { borderColor: "#475569", bgcolor: "#F8FAFC" },
};

interface IBankAccountForm {
  clientSecret: string;
  bankAccountId: number;
  userId: number;
  pharmacy: any;
  pharmacyUsersLoading: boolean;
  setIsEdit: (isEdit: boolean) => void;
  onCancel: () => void;
  onPaymentMethodSaved: () => Promise<void>;
  oldAccountId?: string | null;
}

export const BankAccountForm: React.FunctionComponent<IBankAccountForm> = ({
  clientSecret,
  bankAccountId,
  userId,
  pharmacy,
  pharmacyUsersLoading,
  setIsEdit,
  onCancel,
  onPaymentMethodSaved,
  oldAccountId,
}) => {
  const { addToast } = useContext(ToasterContext);
  const { email: sessionEmail }: any = useContext(UserContext);
  const { token } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [mandate, setMandate] = useState(false);
  const lockedEmail = (sessionEmail || "").trim();
  const [updateBankAccount, { loading }] = useMutationUpdateBankAccount({
    refetchQueries: [
      {
        query: UNIQUE_USER,
        variables: {
          where: { id: userId },
        },
        awaitRefetchQueries: true,
      },
    ],
  });

  const detachOldPaymentMethod = (newPaymentMethodId: string) => {
    if (!oldAccountId || oldAccountId === newPaymentMethodId) return;
    fetch(`${SERVER_URL}/stripe/payment-method/${oldAccountId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {
      // Best-effort cleanup; the BankAccount row was already overwritten.
    });
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Bank account name is Required"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: "",
    },
  });

  const handleMandate = async (e: any) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    const { setupIntent, error }: any = await stripe.confirmUsBankAccountSetup(clientSecret);

    let requestData: any = {
      Pharmacy: {
        connect: {
          id: pharmacy.pharmacyId,
        },
      },
      User: {
        connect: {
          id: userId,
        },
      },
      accountHolderName: { set: data.payment_method.billing_details.name },
      accountId: { set: data.payment_method.id },
      bankName: { set: data.payment_method.us_bank_account.bank_name },
      country: { set: "United states" },
      currency: { set: "USD" },
      last4: { set: data.payment_method.us_bank_account.last4 },
      object: { set: data.payment_method.object },
      routingNumber: { set: data.payment_method.us_bank_account.routing_number },
      status: { set: setupIntent.status === "succeeded" ? "APPROVED" : "PENDING" },
      token: { set: data.id },
    };

    if (setupIntent.next_action) {
      requestData = {
        ...requestData,
        hostedVerificationUrl: {
          set: setupIntent.next_action.verify_with_microdeposits.hosted_verification_url,
        },
      };
    }

    if (error) {
      addToast({ type: NotificationType.Error, title: error.message });
      setIsEdit(false);
    } else if (setupIntent.status === "requires_payment_method") {
      addToast({
        type: NotificationType.Error,
        title: "Confirmation failed. Attempt again with a different payment method.",
      });
      setIsEdit(false);
    } else if (setupIntent.status === "succeeded") {
      try {
        await updateBankAccount({ variables: { data: requestData, where: { id: bankAccountId } } });
        detachOldPaymentMethod(data.payment_method.id);
        addToast({ type: NotificationType.Success, title: "Bank account successfully created." });
        await onPaymentMethodSaved();
      } catch (error: any) {
        addToast({ type: NotificationType.Error, title: error.message });
        setIsEdit(false);
      }
    } else if (setupIntent.next_action?.type === "verify_with_microdeposits") {
      try {
        await updateBankAccount({ variables: { data: requestData, where: { id: bankAccountId } } });
        detachOldPaymentMethod(data.payment_method.id);
        addToast({
          type: NotificationType.Success,
          title: "Bank account successfully created. Please verify your account.",
        });
        await onPaymentMethodSaved();
      } catch (error: any) {
        addToast({ type: NotificationType.Error, title: error.message });
        setIsEdit(false);
      }
    }
  };

  const handleForm = async (formData: any) => {
    setIsLoading(true);

    if (!stripe || !elements) {
      setIsLoading(false);
      return;
    }

    const { setupIntent, error } = await stripe.collectBankAccountForSetup({
      clientSecret: clientSecret,
      params: {
        payment_method_type: "us_bank_account",
        payment_method_data: {
          billing_details: {
            name: formData.name,
            email: lockedEmail,
          },
        },
      },
      expand: ["payment_method"],
    });

    setData(setupIntent);

    if (error) {
      setIsLoading(false);
      addToast({ type: NotificationType.Error, title: error.message });
    } else if (setupIntent.status === "requires_payment_method") {
      setIsLoading(false);
      addToast({
        type: NotificationType.Error,
        title: "Confirmation failed. Attempt again with a different payment method.",
      });
    } else if (setupIntent.status === "requires_confirmation") {
      setIsLoading(false);
      setMandate(true);
    }
  };

  return (
    <Box sx={{ fontFamily: BODY_FONT }}>
      {!mandate ? (
        <Box component="form" onSubmit={handleSubmit(handleForm)} noValidate>
          <Box sx={{ fontSize: 13, color: INK_SOFT, mb: 2.5, lineHeight: 1.5 }}>
            Enter your details to connect a US bank account for ACH payments.
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email Address"
                id="email"
                fullWidth
                required
                disabled
                value={lockedEmail}
                helperText="Filled from your account email"
                InputProps={{ readOnly: true }}
                sx={{
                  ...textFieldSx,
                  "& .MuiOutlinedInput-root": {
                    ...textFieldSx["& .MuiOutlinedInput-root"],
                    bgcolor: LINE_SOFT,
                    color: INK_SOFT,
                    "&.Mui-disabled": {
                      bgcolor: LINE_SOFT,
                      "& fieldset": { borderColor: LINE },
                    },
                    "&.Mui-disabled .MuiOutlinedInput-input": {
                      WebkitTextFillColor: INK_SOFT,
                      color: INK_SOFT,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-disabled": { color: INK_SOFT },
                  "& .MuiFormHelperText-root": { color: INK_SOFT },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Bank Account Name"
                id="name"
                fullWidth
                required
                helperText={errors.name?.message?.toString()}
                {...register("name")}
                error={!!errors.name}
                sx={textFieldSx}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1.5, flexWrap: "wrap" }}>
            <Button variant="outlined" onClick={onCancel} disabled={isLoading} sx={outlinedBtnSx}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading} sx={primaryBtnSx}>
              Add Bank Account
              {isLoading && <CircularProgress sx={{ ml: 1.5 }} size={14} color="inherit" />}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleMandate} noValidate>
          <Box
            sx={{
              fontSize: 14,
              lineHeight: 1.6,
              color: INK,
              "& p": { mt: 0, mb: 1.5 },
            }}
          >
            <p>
              By clicking Accept, you authorize PrimePostRx to credit the bank account specified
              above for any amount owed for charges arising from your use of PrimePostRx’ services
              and/or purchase of products from PrimePostRx, pursuant to PrimePostRx’ website and
              terms, until this authorization is revoked. You may amend or cancel this authorization
              at any time by providing notice to PrimePostRx with 30 (thirty) days notice.
            </p>
            <p>
              If you use PrimePostRx’ services or purchase additional products periodically pursuant
              to PrimePostRx’ terms, you authorize PrimePostRx to debit your bank account
              periodically. Payments that fall outside of the regular debits authorized above will
              only be debited after your authorization is obtained.
            </p>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={() => setMandate(false)}
              disabled={isLoading || loading}
              sx={outlinedBtnSx}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={isLoading || loading}
              sx={outlinedBtnSx}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" sx={primaryBtnSx}>
              Accept
              {(isLoading || loading || pharmacyUsersLoading) && (
                <CircularProgress sx={{ ml: 1.5 }} size={14} color="inherit" />
              )}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BankAccountForm;
